/**
 * ⚡️ KronoMetro WebApp Provider
 *
 * エレガントでモダンなWebアプリケーションプロバイダーコンポーネント
 * 認証、ナビゲーション、レイアウト制御を統合的に管理します
 *
 * 主な機能:
 * - 認証状態に応じたルーティング制御
 * - レスポンシブなダッシュボードレイアウト
 * - スタイリッシュなUI/UXの提供
 * - ダイアログ、スナックバー等の統合管理
 *
 * @module WebAppProvider
 * @description メインプロバイダーコンポーネント
 * @copyright © 2024-present KronoMetro, Co.
 */

"use client";

import * as React from "react";
import { Suspense, useState, useCallback } from "react";
import PropTypes from "prop-types";
import { createTheme } from "@mui/material/styles";
import { useRouter, usePathname, useSearchParams, useParams } from "next/navigation";

import { useSession, signOut, SessionProvider } from "next-auth/react";

import { SnackbarProvider } from "notistack";

import Consts from "@common/config/consts";

import { components } from "@krono-metro/metrojs/client";

const { Loading } = components;

// Providers
import { NextAppProvider } from "@toolpad/core/nextjs";
import { DashboardLayout } from "@toolpad/core/DashboardLayout";
import { PageContainer } from "@toolpad/core/PageContainer";
import { DialogsProvider } from "@toolpad/core";
import { NotificationsProvider } from "@toolpad/core/useNotifications";

import utils from "@metrojs/utils";
import logjs from "@metrojs/logjs";
const log = new logjs("WebAppProvider");

import { AppLogo, ToolbarActions, LogoutButton, SidebarFooter, FooterText, FooterCopyright } from "./styles";
import { useSnackbarEx } from "../hooks/useSnackbarEx";
import { useConfirmEx } from "../hooks/useConfirmEx";
import { useMessageEx } from "../hooks/useMessageEx";

/**
 * アプリケーションのテーマ設定
 * Material-UIのテーマをカスタマイズし、レスポンシブなブレークポイントを定義
 */
const appTheme = createTheme({
  colorSchemes: { light: true, dark: false },
  cssVariables: {
    colorSchemeSelector: "class",
  },
  breakpoints: {
    values: {
      xs: 0,
      sm: 600,
      md: 900,
      lg: 1200,
      xl: 1536,
    },
  },
});

/**
 * スナックバーの種類を定義する定数
 * アプリケーション全体で使用する通知の種類を定義
 * @constant
 * @type {Object}
 */
export const SNACKBAR_SEVERITY = {
  INFO: "info",
  SUCCESS: "success",
  WARNING: "warning",
  ERROR: "error",
};

// 認証関連の定数定義
const AUTH_PAGES = ["/login", "/agreement", "/forgot-password"];

// 認証不要のページを判定する関数
const isPublicPage = (pathname) => {
  return (
    AUTH_PAGES.includes(pathname) ||
    pathname.startsWith("/login") ||
    pathname.startsWith("/seibi_request") ||
    pathname.startsWith("/open_mitsumori")
  );
};

// コンテキストの作成
export const WebAppContext = React.createContext(null);

// コンテキストを使用するためのフック
export function useWebAppContext() {
  const context = React.useContext(WebAppContext);
  if (!context) {
    throw new Error("useWebAppContext must be used within a WebAppProvider");
  }
  return context;
}

/**
 * WebAppProviderの内部コンポーネント
 * レイアウトのメインロジックを管理し、認証状態に応じたルーティングを制御
 *
 * @param {Object} props - コンポーネントのプロパティ
 * @param {Function} props.window - windowオブジェクトを提供する関数
 * @param {React.ReactNode} props.children - 子要素
 * @param {Function} props.navigation - ナビゲーションアイテムを生成する関数
 * @param {any} props.initData - 初期データ
 * @param {boolean} props.isInitDataLoading - 初期データのローディング状態
 * @param {Function} props.showConfirm - 確認ダイアログを表示する関数
 * @param {Function} props.showMessage - メッセージダイアログを表示する関数

 */
function WebAppProviderInner(props) {
  const { window, children, navigation, initData, isInitDataLoading, onInitDataFetch, isDashboardLayout = true, homePath } = props;
  const nextRouter = useRouter();
  const pathname = usePathname();
  const { data: session, status, update: updateSession } = useSession({ required: false });
  const appWindow = window ? window() : undefined;
  let searchParams = useSearchParams(); // URLのクエリパラメータ
  const params = useParams(); // URLのパラメータ
  searchParams = React.useMemo(() => Object.fromEntries(searchParams.entries()), [searchParams]);

  //log.debug("params", params);
  //log.debug("searchParams", searchParams);

  // 各種フックの初期化
  const [isLoading, setIsLoading] = useState(false);
  const [pageTitle, setPageTitle] = useState("");

  //const { showError, showWarning, showSuccess } = useSnackbarEx();
  const { showConfirm, renderConfirmDialog } = useConfirmEx();
  const { showAlert, showInfo, showError, showWarning, showSuccess, renderMessageDialog } = useMessageEx();

  // useConfirmExとuseAlertExはpropsから受け取る（DialogsProvider内で呼び出される）
  // 追加: 即時未認証化のためのフラグ
  // と user 参照
  const [forceLogout, setForceLogout] = useState(false);
  const user = forceLogout ? null : session?.user;

  // 現在のページ状態を判定
  const isPublic = isPublicPage(pathname);

  // ルーター設定のメモ化
  const router = React.useMemo(
    () => ({
      pathname: pathname || "/",
      searchParams, // 実値をそのまま渡す
      navigate: (path) => {
        nextRouter.push(path);
      },
    }),
    [pathname, nextRouter, searchParams],
  );

  const showLoading = useCallback(() => {
    setIsLoading(true);
  }, [setIsLoading]);

  const hideLoading = useCallback(() => {
    setIsLoading(false);
  }, [setIsLoading]);

  // ナビゲーション処理
  const [isPending, startTransition] = React.useTransition();
  const handleNavigation = (path) => {
    showLoading();
    startTransition(() => {
      nextRouter.push(path);
    });
  };
  React.useEffect(() => {
    if (!isPending) hideLoading();
  }, [isPending, hideLoading]);

  // ログアウト処理
  const logout = useCallback(async () => {
    try {
      showLoading();
      setForceLogout(true);
      const result = await signOut({
        redirect: false,
        callbackUrl: "/login",
      });
      nextRouter.replace(result?.url || "/login");
    } catch {
      showError("ログアウトに失敗しました");
      nextRouter.replace("/login");
    } finally {
      hideLoading();
    }
  }, [showLoading, showError, hideLoading, nextRouter]);

  const stableActions = React.useMemo(
    () => ({
      setPageTitle,
      showLoading,
      hideLoading,
      showError,
      showWarning,
      showInfo,
      showSuccess,
      showConfirm: showConfirm,
      showAlert: showAlert,
      onInitDataFetch: onInitDataFetch,
      logout: logout,
      updateSession,
    }),
    [logout, setPageTitle, showLoading, hideLoading, showError, showWarning, showInfo, showSuccess, showConfirm, showAlert, onInitDataFetch, updateSession],
  );

  // コンテキストの値を準備
  const contextValue = React.useMemo(() => {
    return {
      params,
      searchParams,
      state: {
        user: user,
        initData,
        isInitDataLoading,
        pageTitle: pageTitle,
        isLoading: isLoading,
      },
      actions: stableActions,
    };
  }, [params, user, initData, isInitDataLoading, pageTitle, isLoading, stableActions]);

  // ナビゲーションアイテムの取得
  const navigationItems = navigation({ contextValue, handleNavigation });

  // 認証状態に基づくリダイレクト制御
  React.useEffect(() => {
    if (status === "loading") return;

    const run = async () => {
      if (status === "unauthenticated" && isPublic) return;
      if (user) {
        // 公開ページでも /seibi_request/* /open_mitsumori は自動ログイン→詳細遷移のため残す（ここでホームへ飛ばすとリダイレクトできない）
        const isExternalOpenEntry =
          pathname.startsWith("/seibi_request") || pathname.startsWith("/open_mitsumori");
        if (isPublic && pathname !== "/" && !isExternalOpenEntry) {
          const homePath = props.homePath ?? Consts.ROUTES.HOME;
          await nextRouter.replace(homePath);
        }
      } else {
        if (!isPublic) {
          log.debug(`認証状態に基づくリダイレクト制御 isPublic: ${isPublic} pathname: ${pathname}`, params);
          const url = utils.hasOwn(params, "mode") ? `/login/${params.mode}` : "/login";
          //await nextRouter.replace("/login");
          await nextRouter.replace(url);
        }
      }
    };
    run();
  }, [user, status, isPublic, pathname, nextRouter, props.homePath]);

  // 認証中の表示制御
  if (status === "loading") {
    log.debug("Loading - 認証中です");
    return <Loading show={true} />;
  }

  // 認証ページの表示制御
  if (isPublic) {
    if (!children) {
      return <Loading show={true} />;
    }
    return (
      <WebAppContext.Provider value={contextValue}>
        {renderConfirmDialog()}
        {renderMessageDialog()}
        {children}
      </WebAppContext.Provider>
    );
  }

  // 未認証時の表示制御
  if (!user && !isPublic) {
    log.debug("Loading - 未認証ユーザーがアクセスしました");
    return <Loading show={true} />;
  }

  if (isDashboardLayout === false) {
    if (!children) {
      return <Loading show={true} />;
    }
    return (
      <WebAppContext.Provider value={contextValue}>
        {renderConfirmDialog()}
        {renderMessageDialog()}
        {isLoading && <Loading show={isLoading} />}
        <NextAppProvider navigation={navigationItems} router={router} theme={appTheme} window={appWindow}>
          {children}
        </NextAppProvider>
      </WebAppContext.Provider>
    );
  }

  // メインレイアウトの描画
  //
  return (
    <WebAppContext.Provider value={contextValue}>
      {renderConfirmDialog()}
      {renderMessageDialog && renderMessageDialog()}
      {isLoading && <Loading show={isLoading} />}
      <NextAppProvider navigation={navigationItems} router={router} theme={appTheme} window={appWindow}>
        <DashboardLayout
          title="MetroJS"
          sx={{
            "& main": {
              //flex: "none",
              height: "auto",
              width: "100%",
              //overflow: "visible",
            },
          }}
          slots={{
            appTitle: () => <AppLogo src="/images/do_logo_only.svg" alt="DirectOrder" style={{ maxHeight: "48px", width: "150px" }} />,

            breadcrumbs: () => null,
            // ページタイトルを非表示にする
            pageTitle: () => null,
            toolbarActions: () => (
              <ToolbarActions>
                <LogoutButton onClick={logout}>ログアウト</LogoutButton>
              </ToolbarActions>
            ),
            sidebarFooter: () => (
              <SidebarFooter>
                <FooterText>{Consts.APP_NAME}</FooterText>
                <FooterCopyright>© 2025 All Rights Reserved</FooterCopyright>
              </SidebarFooter>
            ),
          }}
        >
          <PageContainer
            breadcrumbs={[]}
            slotProps={{
              // タイトル を非表示にする
              header: {
                title: "",
              },
            }}
            sx={{
              // MuiContainer-root のスタイルをここで変更
              // 例: maxWidth: "lg", py: 2, px: 0 など
              marginLeft: 1,
            }}
          >
            {children}
          </PageContainer>
        </DashboardLayout>
      </NextAppProvider>
    </WebAppContext.Provider>
  );
}

/**
 * WebAppProviderコンポーネント
 * アプリケーション全体のプロバイダーを統合し、認証状態を管理
 *
 * @param {Object} props - コンポーネントのプロパティ
 * @param {Function} props.window - windowオブジェクトを提供する関数
 * @param {React.ReactNode} props.children - 子要素
 * @param {Function} props.navigation - ナビゲーションアイテムを生成する関数
 * @param {any} props.initData - 初期データ
 * @param {boolean} props.isInitDataLoading - 初期データのローディング状態
 * @param {Function} props.onInitDataFetch - 初期データの取得関数
 */
function WebAppProvider(props) {
  return (
    <SessionProvider refetchInterval={0} refetchOnWindowFocus={true} refetchWhenOffline={false}>
      <SnackbarProvider>
        <NotificationsProvider>
          <Suspense fallback={<Loading show={true} />}>
            <WebAppProviderInner {...props} />
          </Suspense>
        </NotificationsProvider>
      </SnackbarProvider>
    </SessionProvider>
  );
}

// PropTypes 定義（開発時の props 検証用）
WebAppProviderInner.propTypes = {
  window: PropTypes.func,
  children: PropTypes.node,
  navigation: PropTypes.func.isRequired,
  initData: PropTypes.any,
  isInitDataLoading: PropTypes.bool,
  onInitDataFetch: PropTypes.func,
  isDashboardLayout: PropTypes.bool,
  /** 認証済みユーザーが公開ページ（ログイン等）にいる場合のリダイレクト先。未指定時は Consts.ROUTES.HOME（/agreement） */
  homePath: PropTypes.string,
};

WebAppProvider.propTypes = {
  window: PropTypes.func,
  children: PropTypes.node,
  navigation: PropTypes.func.isRequired,
  initData: PropTypes.any,
  isInitDataLoading: PropTypes.bool,
  onInitDataFetch: PropTypes.func,
  /** 認証済みユーザーが公開ページにいる場合のリダイレクト先（例: admin は /dashboard） */
  homePath: PropTypes.string,
};

export default WebAppProvider;
